import { useEffect, useState, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CurrencyInput from 'react-currency-input-field';

import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';

import { editarBreadcrumb } from '../../../actions/breadcrumb';
import { obtenerPerfilEmpresarial, guardarPerfilEmpresarial, buscarRegimenesFiscalesConfiguracion, buscarGirosConfiguracion } from '../../../actions/configuracion';
import { setAlert } from '../../../actions/alert';

import { CustomValueContainer } from '../../../components/react_select/ReactSelect';

const Perfil = () => {

    let rutasBread = [
        {
            activo: true,
            nombre: 'Perfil empresarial',
            path: '/perfil_empresarial'
        }
    ];

    const dispatch = useDispatch();

    const regimenes_fiscales = useSelector(state => state.configuracion.regimenes_fiscales);
    const giros = useSelector(state => state.configuracion.giros);

    const { empresa } = useSelector(state => state.auth);
    
    const [perfilData, setPerfilData] = useState({
        nombre_comercial: '',
        razon_social: '',
        rfc: '',
        curp: '',
        tipo_persona: null,
        regimen_fiscal: null,
        giro: null,
        representante_legal: '',
        puesto: '',
        domicilio_fiscal: '',
        telefono: '',
        email_empresarial: '',
        web: '',
        fecha_constitucion: null,
        numero_empleados: '',
        minimo_ingresos_anuales: '',
        maximo_ingresos_anuales: ''
    });

    const [telefonoDialCode, setTelefonoDialCode] = useState('52');

    const [tipoPersonaData, setTipoPersonaData] = useState(null);
    const tiposPersonaFilter = [
        { value: 0, label: 'Física' }, 
        { value: 1, label: 'Moral' }
    ];

    const [regimenFiscalData, setRegimenFiscalData] = useState(null);
    const [regimenesFiscalesFilter, setRegimenesFiscalesFilter] = useState([]);

    const [giroData, setGiroData] = useState(null);
    const [girosFilter, setGirosFilter] = useState([]);

    let styles_select = {
        menuPortal: base => ({ ...base, zIndex: 9999 }), 
        container: (provided, state) => ({
            ...provided, 
        }),
        control: (provided, state) => ({
            ...provided,
            paddingTop: '5px',
            paddingBottom: '5px',
        }),
        valueContainer: (provided, state) => ({
            ...provided,
            overflow: "visible", 
            marginTop: "10px"
        }),
        placeholder: (provided, state) => ({
            ...provided,
            position: "absolute",
            top: state.hasValue || state.selectProps.inputValue ? -15 : "0%",
            transition: "top 0.1s, font-size 0.1s",
            fontSize: (state.hasValue || state.selectProps.inputValue) && 13
        })
    };

    /* Validación RFC */
    const validarRFC = (rfc, tipo_persona) => {
        const regex = /^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/;
        if (!regex.test(rfc)) return 'El formato del RFC no es válido';
        if (rfc === 'XAXX010101000') return 'El RFC genérico no es aceptado';
        if (tipo_persona === 1 && rfc.length !== 12) return 'El RFC no corresponde al tipo de persona seleccionado';
        if (tipo_persona === 0 && rfc.length !== 13) return 'El RFC no corresponde al tipo de persona seleccionado';
        const letras = tipo_persona === 1 ? 3 : 4;
        const mes = parseInt(rfc.slice(letras + 2, letras + 4), 10);
        const dia = parseInt(rfc.slice(letras + 4, letras + 6), 10);
        if (mes < 1 || mes > 12 || dia < 1 || dia > 31) return 'La fecha en el RFC no es válida';
        return null;
    };

    /* Guardar perfil */
    const handleInputChange = ({ target }) => {
        let value = target.value;
        if (target.name === 'rfc') {
            value = value.toUpperCase().replace(/[\s-]/g, '');
        }
        setPerfilData({
            ...perfilData,
            [target.name]: value
        });
    }

    const handleNumberChange = (value, name) => {
        setPerfilData({
            ...perfilData,
            [name]: value || ''
        });
    }

    const handleDatePicker = (name, value) => {
        setPerfilData({
            ...perfilData,
            [name]: value
        });
    }

    const handleSelectTipoPersona = (option) => {
        setTipoPersonaData(option);
        setRegimenFiscalData(null);

        setPerfilData({
            ...perfilData,
            tipo_persona: option != null ? option.value : null, 
            regimen_fiscal: null
        });

        dispatch(buscarRegimenesFiscalesConfiguracion(option?.value));
    }

    const handleSelectRegimenFiscal = (option) => {
        setRegimenFiscalData(option);

        setPerfilData({
            ...perfilData,
            regimen_fiscal: option != null ? option.value : null
        });
    }

    const handleSelectGiro = (option) => {
        setGiroData(option);

        setPerfilData({
            ...perfilData,
            giro: option != null ? option.value : null
        });
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (perfilData.tipo_persona === null || perfilData.tipo_persona === undefined) {
            dispatch(setAlert('El tipo de persona es obligatorio', 'danger'));
            return;
        }

        if (!perfilData.rfc) {
            dispatch(setAlert('El RFC es obligatorio', 'danger'));
            return;
        }

        const rfcError = validarRFC(perfilData.rfc, perfilData.tipo_persona);
        if (rfcError) {
            dispatch(setAlert(rfcError, 'danger'));
            return;
        }

        if (perfilData.tipo_persona === 1) {
            if (!perfilData.razon_social) {
                dispatch(setAlert('La razón social es obligatoria para persona moral', 'danger'));
                return;
            }
            if (!perfilData.regimen_fiscal) {
                dispatch(setAlert('El régimen fiscal es obligatorio para persona moral', 'danger'));
                return;
            }
        }

        if (perfilData.telefono && (perfilData.telefono.length - telefonoDialCode.length) !== 10) {
            dispatch(setAlert('El teléfono empresarial debe tener 10 dígitos sin contar la lada.', 'danger'));
            return;
        }

        try {
            dispatch(guardarPerfilEmpresarial(perfilData));
        } catch (error) {
            return false;
        }
    }

    useEffect(() => {
        dispatch(obtenerPerfilEmpresarial());
        dispatch(buscarGirosConfiguracion());
    }, []);

    useEffect(() => {
        let options = [];

        for (var i = 0; i < regimenes_fiscales.listado?.length; i++) {
            options.push({ value: regimenes_fiscales.listado[i]._id, label: regimenes_fiscales.listado[i].nombre });
        }

        setRegimenesFiscalesFilter(options);
    }, [regimenes_fiscales]);

    useEffect(() => {
        let options = [];

        for (var i = 0; i < giros.listado?.length; i++) {
            options.push({ value: giros.listado[i]._id, label: giros.listado[i].nombre });
        }

        setGirosFilter(options);
    }, [giros]);

    useEffect(() => {
        if(empresa?._id){
            setPerfilData({
                ...perfilData,
                id: empresa._id,
                nombre_comercial: empresa.nombre_comercial || '',
                razon_social: empresa.razon_social || '',
                rfc: empresa.rfc || '',
                curp: empresa.curp || '',
                tipo_persona: empresa.tipo_persona ?? null,
                regimen_fiscal: empresa.regimen_fiscal?._id || null,
                giro: empresa.giro?._id || null,
                representante_legal: empresa.representante_legal || '',
                puesto: empresa.puesto || '',
                domicilio_fiscal: empresa.domicilio_fiscal || '',
                telefono: empresa.telefono || '',
                email_empresarial: empresa.email || '',
                web: empresa.web || '',
                fecha_constitucion: empresa.fecha_constitucion || null,
                numero_empleados: empresa.numero_empleados || '',
                minimo_ingresos_anuales: empresa.minimo_ingresos_anuales || '',
                maximo_ingresos_anuales: empresa.maximo_ingresos_anuales || ''
            });

            if(empresa.regimen_fiscal?._id){
                setRegimenFiscalData({ value: empresa.regimen_fiscal._id, label: empresa.regimen_fiscal.nombre });
            }

            if(empresa.giro?._id){
                setGiroData({ value: empresa.giro._id, label: empresa.giro.nombre });
            }

            let tipo_persona_asignado = tiposPersonaFilter.find(tipo => tipo.value == empresa.tipo_persona);
            if(tipo_persona_asignado){
                setTipoPersonaData({ value: tipo_persona_asignado.value, label: tipo_persona_asignado.label });
                dispatch(buscarRegimenesFiscalesConfiguracion(empresa.tipo_persona));
            }
        }
    }, [empresa]);    

    useEffect(() => {
        dispatch(editarBreadcrumb(rutasBread));
    }, [rutasBread]);

    return(
        <Fragment>
            <HelmetProvider>
                <Helmet>
                    <style>{`
                        input[type="file"] {
                            display: none;
                        }
                        
                        .custom-file-upload {
                            border-radius: 50%;
                            display: inline-block;
                            position: relative;
                            padding: 0px;
                            cursor: pointer;
                            background: #fff;
                            margin-bottom: 25px;
                            border: 5px solid #111F62;
                        }
                        
                        .img-wrap{
                            position: relative;
                            width: 200px;
                            height: 200px;
                            overflow: hidden;
                            border-radius: 50%;
                        }

                        .img-upload:before{
                            content: "";
                            font-family: "Font Awesome 6 Free" !important;
                            font-weight: 900;
                            font-size: 90px;
                            position: absolute;
                            padding-top: 50px;
                            top: 50%;
                            left: 50%;
                            transform: translate(-50%, -50%);
                            text-align: center;
                            color: #21B4E9;
                            width: 200px;
                            height: 200px;
                            border-radius: 50%;
                            opacity: 0;
                            transition: .5s ease;
                            background-color: #fff;
                        }

                        .img-upload:hover:before{
                            opacity: 1;
                        }

                        #img-photo {
                            width: auto;
                            height: 100%;
                        }
                    `}</style>
                </Helmet>
            </HelmetProvider>

            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="title-section my-5">Información empresarial</h3>

                    <div className="row">
                        <div className="col-md-3 form-group mb-3">
                            <div className="input-group">
                                <div className="form-floating">
                                    <input type="text" className="form-control" placeholder="Nombre comercial" id="nombre_comercial" name="nombre_comercial" value={perfilData.nombre_comercial || ''} onChange={e => handleInputChange(e)}/>
                                    <label htmlFor="nombre_comercial">Nombre comercial</label>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 form-group mb-3">
                            <div className="input-group">
                                <div className="form-floating">
                                    <input type="text" className="form-control" placeholder="Razón social" id="razon_social" name="razon_social" value={perfilData.razon_social || ''} onChange={e => handleInputChange(e)}/>
                                    <label htmlFor="razon_social">Razón social</label>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 form-group mb-3">
                            <div className="input-group">
                                <div className="form-floating">
                                    <input type="text" className="form-control" placeholder="RFC" id="rfc" name="rfc" value={perfilData.rfc || ''} onChange={e => handleInputChange(e)}/>
                                    <label htmlFor="rfc">RFC</label>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 form-group mb-3">
                            <div className="input-group">
                                <div className="form-floating">
                                    <input type="text" className="form-control" placeholder="CURP" id="curp" name="curp" value={perfilData.curp || ''} onChange={e => handleInputChange(e)}/>
                                    <label htmlFor="curp">CURP</label>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-2 form-group mb-3">
                            <div className="input-group h-100">
                                <Select
                                    id="tipo_persona"
                                    name="tipo_persona"
                                    className="select-group"
                                    classNamePrefix="react-select"
                                    placeholder="Tipo de persona"
                                    isClearable={true}
                                    options={tiposPersonaFilter}
                                    value={tipoPersonaData}
                                    onChange={(value) => handleSelectTipoPersona(value)}
                                    menuPortalTarget={document.body}
                                    components={{ ValueContainer: CustomValueContainer }}
                                    styles={{ ...styles_select }}
                                />
                            </div>
                        </div>
                        <div className="col-md-6 form-group mb-3">
                            <div className="input-group h-100">
                                <Select
                                    id="regimen_fiscal"
                                    name="regimen_fiscal"
                                    className="select-group"
                                    classNamePrefix="react-select"
                                    placeholder="Régimen fiscal"
                                    isClearable={true}
                                    options={regimenesFiscalesFilter}
                                    value={regimenFiscalData}
                                    onChange={(value) => handleSelectRegimenFiscal(value)}
                                    menuPortalTarget={document.body}
                                    components={{ ValueContainer: CustomValueContainer }}
                                    styles={{ ...styles_select }}
                                />
                            </div>
                        </div>
                        <div className="col-md-4 form-group mb-3">
                            <div className="input-group h-100">
                                <Select
                                    id="giro"
                                    name="giro"
                                    className="select-group"
                                    classNamePrefix="react-select"
                                    placeholder="Actividad empresarial"
                                    isClearable={true}
                                    options={girosFilter}
                                    value={giroData}
                                    onChange={(value) => handleSelectGiro(value)}
                                    menuPortalTarget={document.body}
                                    components={{ ValueContainer: CustomValueContainer }}
                                    styles={{ ...styles_select }}
                                />
                            </div>
                        </div>
                        
                        <hr />
                        
                        <div className="col-md-3 form-group mb-3">
                            <div className="input-group">
                                <div className="form-floating">
                                    <input type="text" className="form-control" placeholder="Representante legal" id="representante_legal" name="representante_legal" value={perfilData.representante_legal || ''} onChange={e => handleInputChange(e)}/>
                                    <label htmlFor="representante_legal">Representante legal</label>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 form-group mb-3">
                            <div className="input-group">
                                <div className="form-floating">
                                    <input type="text" className="form-control" placeholder="Puesto" id="puesto" name="puesto" value={perfilData.puesto || ''} onChange={e => handleInputChange(e)}/>
                                    <label htmlFor="puesto">Puesto</label>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 form-group mb-3">
                            <div className="input-group">
                                <div className="form-floating">
                                    <input type="text" className="form-control" placeholder="Domicilio fiscal" id="domicilio_fiscal" name="domicilio_fiscal" value={perfilData.domicilio_fiscal || ''} onChange={e => handleInputChange(e)}/>
                                    <label htmlFor="domicilio_fiscal">Domicilio fiscal</label>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 form-group mb-3">
                            <PhoneInput
                                country="mx"
                                preferredCountries={['mx', 'us']}
                                enableSearch={true}
                                placeholder="Teléfono"
                                value={perfilData.telefono || ''}
                                onChange={(value, countryData) => {
                                    setPerfilData({ ...perfilData, telefono: value });
                                    setTelefonoDialCode(countryData.dialCode);
                                }}
                                inputClass="form-control"
                                containerClass="w-100"
                                inputStyle={{ width: '100%', height: 'calc(3.5rem + 2px)' }}
                                buttonStyle={{ height: 'calc(3.5rem + 2px)' }}
                            />
                        </div>
                        <div className="col-md-3 form-group mb-3">
                            <div className="input-group">
                                <div className="form-floating">
                                    <input type="text" className="form-control" placeholder="Correo" id="email_empresarial" name="email_empresarial" value={perfilData.email_empresarial || ''} onChange={e => handleInputChange(e)}/>
                                    <label htmlFor="email_empresarial">Correo</label>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 form-group mb-3">
                            <div className="input-group">
                                <div className="form-floating">
                                    <input type="text" className="form-control" placeholder="Sitio web" id="web" name="web" value={perfilData.web || ''} onChange={e => handleInputChange(e)}/>
                                    <label htmlFor="web">Sitio web</label>
                                </div>
                            </div>
                        </div>

                        <hr />

                        <div className="col-md-3 form-group mb-3">
                            <div className="input-group">
                                <div className="form-floating">
                                    <DatePicker
                                        id="fecha_constitucion"
                                        name="fecha_constitucion"
                                        className="form-control text-center input-datepícker"
                                        dateFormat="dd/MM/yyyy"
                                        locale="es-ES"
                                        peekNextMonth
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                        selected={ perfilData.fecha_constitucion != null ? new Date(perfilData.fecha_constitucion) : '' }
                                        onChange={ date => handleDatePicker('fecha_constitucion', date) }
                                    />
                                    <label className="label-datepicker" htmlFor="fecha_constitucion">Fecha de constitución</label>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 form-group mb-3">
                            <div className="input-group">
                                <div className="form-floating">
                                    <input type="number" className="form-control" placeholder="Número de empleados" id="numero_empleados" name="numero_empleados" value={perfilData.numero_empleados || ''} onChange={e => handleInputChange(e)}/>
                                    <label htmlFor="numero_empleados">Número de empleados</label>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 form-group mb-3">
                            <div className="input-group">
                                <div className="form-floating">
                                    <CurrencyInput
                                        id="minimo_ingresos_anuales"
                                        name="minimo_ingresos_anuales"
                                        className="form-control text-center"
                                        value={perfilData.minimo_ingresos_anuales || ''}
                                        decimalsLimit={2}
                                        groupSeparator=","
                                        decimalSeparator="."
                                        onValueChange={(value) => handleNumberChange(value, 'minimo_ingresos_anuales')}
                                    />
                                    <label htmlFor="minimo_ingresos_anuales">Mínimo de ingresos anuales</label>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 form-group mb-3">
                            <div className="input-group">
                                <div className="form-floating">
                                    <CurrencyInput
                                        id="maximo_ingresos_anuales"
                                        name="maximo_ingresos_anuales"
                                        className="form-control text-center"
                                        value={perfilData.maximo_ingresos_anuales || ''}
                                        decimalsLimit={2}
                                        groupSeparator=","
                                        decimalSeparator="."
                                        onValueChange={(value) => handleNumberChange(value, 'maximo_ingresos_anuales')}
                                    />
                                    <label htmlFor="maximo_ingresos_anuales">Máximo de ingresos anuales</label>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="row">
                        <div className="col-md-12 mt-4">
                            <button type="button" className="btn btn-success float-end" onClick={handleSubmit}>Guardar perfil</button>   
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    )
}

export default Perfil;